// @ts-check
// Novo campo e edição de campo (doc 11, 3.6.5 e 3.6.6): tipo, ícone e nome, descrição, opções de seleção e
// zona de perigo. Campos do pacote podem mudar de rótulo e ser desativados, nunca excluídos.
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';
import { computeMetadataNameFromLabel, computeOptionValueFromLabel } from '../../../../../shared/metadata/metadata-names.js';
import { FUNNEL_STAGE_FIELD_PATHS } from '../../../../../shared/metadata/funnel-stages.js';
import { Link, navigate } from '../../../app/router.js';
import { NotFoundPage } from '../../../app/not-found-page.js';
import { createFieldMetadata, deleteFieldMetadata, fetchFieldOptionUsage, updateFieldMetadata } from '../../../data/data-model.js';
import { isTenantModuleEnabled, objectsState } from '../../../data/metadata-store.js';
import { getFieldIcon } from '../../../lib/field-metadata.js';
import { Button } from '../../../ui/button.js';
import { Icon } from '../../../ui/icon.js';
import { Modal } from '../../../ui/modal.js';
import { showSuccessSnackbar } from '../../../ui/snackbar.js';
import { t, translateMetadataLabel } from '../../../i18n/index.js';
import { PageCard, PageHeader } from '../../page/page-card.js';
import { SettingsSection } from '../settings-pages.js';
import {
  DangerZone, DataModelBreadcrumb, canEditDataModel, findObjectByNamePlural, objectBreadcrumbItem, rootBreadcrumbItem, showDataModelError,
} from './data-model-shared.js';
import { FieldCustomizationSections } from './field-customization.js';
import { buildCustomizationChanges, toCustomizationDraft } from './field-customization-draft.js';
import { OptionRemovalModal } from './option-removal-modal.js';
import { IconPicker } from './pickers.js';
import { SelectOptionsEditor, findRemovedOptions, toApiOptions, toDraftOptions, validateDraftOptions } from './select-options-editor.js';
import { isHiddenModuleMetadata } from '../../../lib/module-metadata-visibility.js';

/**
 * @typedef {import('../../../lib/field-metadata.js').ObjectMetadata} ObjectMetadata
 * @typedef {import('../../../lib/field-metadata.js').FieldMetadata} FieldMetadata
 * @typedef {import('./select-options-editor.js').DraftOption} DraftOption
 */

// ordem e categorias do seletor de tipo do Twenty (doc 11, 3.6.5), só com os tipos que o motor cria
/** @type {ReadonlyArray<[string, string[]]>} */
const FIELD_TYPE_CATEGORIES = Object.freeze([
  ['basic', ['TEXT', 'NUMBER', 'BOOLEAN', 'DATE_TIME', 'DATE', 'SELECT', 'MULTI_SELECT', 'RATING', 'CURRENCY', 'EMAILS', 'LINKS', 'PHONES', 'FULL_NAME', 'ADDRESS', 'RICH_TEXT', 'FILES']],
  ['relation', ['RELATION']],
  ['advanced', ['UUID', 'RAW_JSON', 'ARRAY']],
]);
const SELECT_TYPES = ['SELECT', 'MULTI_SELECT'];
// tipos cujo nome de API pode mudar depois de criado (coluna simples, sem sub-campos materializados)
const RENAMABLE_TYPES = ['TEXT', 'NUMBER', 'BOOLEAN', 'DATE_TIME', 'DATE', 'SELECT', 'MULTI_SELECT', 'RATING', 'EMAILS', 'LINKS', 'PHONES', 'ADDRESS', 'RICH_TEXT', 'RAW_JSON', 'ARRAY', 'FILES', 'UUID'];
const DELETE_CONFIRMATION_WORD = 'excluir';
const DEFAULT_TYPE_ICON = { RELATION: 'IconRelationManyToOne' };

/** @param {ObjectMetadata} objectMetadata @param {FieldMetadata} field */
const isFunnelStageField = (objectMetadata, field) =>
  FUNNEL_STAGE_FIELD_PATHS.some(([objectName, fieldName]) => objectName === objectMetadata.nameSingular && fieldName === field.name);

/** "'VALOR'" vira "VALOR" (convenção de default do Twenty, doc 04, 2.3). */
const unquote = (/** @type {unknown} */ value) => (typeof value === 'string' ? value.replace(/^'(.*)'$/, '$1') : null);

/**
 * Passo 1 da criação: escolher o tipo.
 * @param {{ objectMetadata: ObjectMetadata, onSelect: (type: string) => void }} props
 */
function FieldTypeStep({ objectMetadata, onSelect }) {
  return html`<div class="settings-page">
    ${FIELD_TYPE_CATEGORIES.map(([category, types]) => html`<${SettingsSection} key=${category} title=${t(`dataModel.fieldTypeCategories.${category}.title`)}
      description=${t(`dataModel.fieldTypeCategories.${category}.description`)}>
      <div class="data-model-type-grid">
        ${types.map((type) => html`<button type="button" key=${type} class="data-model-type-card" onClick=${() => onSelect(type)}>
          <${Icon} name=${getFieldIcon(/** @type {any} */ ({ type }))} size=${16} />
          <span>${t(`fieldTypes.${type}`)}</span>
        </button>`)}
      </div>
    <//>`)}
    <p class="data-model-empty-hint">${t('dataModel.fields.typeHint', { object: translateMetadataLabel(objectMetadata.labelSingular) })}</p>
  </div>`;
}

/**
 * Formulário comum à criação (passo 2) e à edição.
 * @param {{ objectMetadata: ObjectMetadata, field: FieldMetadata | null, type: string, onChangeType?: () => void }} props
 */
function FieldForm({ objectMetadata, field, type, onChangeType }) {
  const isCreate = field === null;
  const isEditable = canEditDataModel();
  const isSystemField = Boolean(field?.isSystem);
  const isFunnelField = field !== null && isFunnelStageField(objectMetadata, field);
  const originalLabel = field ? translateMetadataLabel(field.label) : '';
  const [icon, setIcon] = useState(field?.icon ?? DEFAULT_TYPE_ICON[type] ?? getFieldIcon(/** @type {any} */ ({ type })));
  const [label, setLabel] = useState(originalLabel);
  const [description, setDescription] = useState(translateMetadataLabel(field?.description ?? ''));
  const [isNameSynced, setIsNameSynced] = useState(isCreate);
  const [name, setName] = useState(field?.name ?? '');
  const [options, setOptions] = useState(/** @type {DraftOption[]} */ (
    field ? toDraftOptions(field.options) : SELECT_TYPES.includes(type) ? toDraftOptions([{ value: computeOptionValueFromLabel(t('dataModel.options.newOptionLabel', { number: 1 })), label: t('dataModel.options.newOptionLabel', { number: 1 }), color: 'green' }]) : []
  ));
  const [defaultValue, setDefaultValue] = useState(/** @type {string | null} */ (field && type === 'SELECT' ? unquote(field.defaultValue) : null));
  const [usage, setUsage] = useState(/** @type {Record<string, number> | null} */ (null));
  const [relationType, setRelationType] = useState('MANY_TO_ONE');
  const relationTargets = objectsState.value.filter((object) => !object.isSystem).sort((first, second) => translateMetadataLabel(first.labelSingular).localeCompare(translateMetadataLabel(second.labelSingular)));
  const [targetObjectId, setTargetObjectId] = useState(relationTargets.find((object) => object.nameSingular === 'person')?.id ?? relationTargets[0]?.id ?? '');
  const [inverseLabel, setInverseLabel] = useState(translateMetadataLabel(objectMetadata.labelPlural));
  const [removal, setRemoval] = useState(/** @type {{ removed: FieldMetadata['options'], usage: Record<string, number> } | null} */ (null));
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [confirmation, setConfirmation] = useState('');
  const [customization, setCustomization] = useState(() => toCustomizationDraft(field, type));

  useEffect(() => {
    if (!field || !SELECT_TYPES.includes(type) || !isEditable) return;
    fetchFieldOptionUsage(field.id).then(setUsage).catch(() => setUsage(null));
  }, [field?.id]);

  const canRename = isCreate || (Boolean(field?.isCustom) && RENAMABLE_TYPES.includes(type));
  // campos de sistema e relações não têm personalização; o padrão de seleção múltipla só aponta para opções que existem
  const hasCustomization = type !== 'RELATION' && !isSystemField;
  const draftOptionValues = new Set(options.map((option) => option.value));
  const customizationResult = hasCustomization
    ? buildCustomizationChanges(field, type, type === 'MULTI_SELECT'
      ? { ...customization, defaultDraft: { ...customization.defaultDraft, values: customization.defaultDraft.values.filter((value) => draftOptionValues.has(value)) } }
      : customization)
    : { changes: {}, error: null };
  const apiName = isNameSynced ? computeMetadataNameFromLabel(label) : name;
  const takenNames = new Set(objectMetadata.fields.filter((candidate) => candidate.id !== field?.id).flatMap((candidate) => [candidate.name, `${candidate.name}Id`]));
  let error = null;
  if (label.trim()) {
    if (canRename && !/^[a-z][a-zA-Z0-9]*$/.test(apiName)) error = t('dataModel.errors.invalidApiName');
    else if (canRename && takenNames.has(apiName)) error = t('dataModel.errors.fieldExists', { name: apiName });
    else if (SELECT_TYPES.includes(type) && !isFunnelField) error = validateDraftOptions(options);
  }
  const hasCustomizationChanges = Object.keys(customizationResult.changes).length > 0;
  const isDirty = isCreate || label !== originalLabel || description !== translateMetadataLabel(field?.description ?? '') || icon !== (field?.icon ?? icon)
    || (canRename && apiName !== field?.name)
    || (SELECT_TYPES.includes(type) && !isFunnelField && (JSON.stringify(toApiOptions(options)) !== JSON.stringify(toApiOptions(toDraftOptions(field?.options)))
      || defaultValue !== unquote(field?.defaultValue)))
    || hasCustomizationChanges;
  const canSave = isEditable && Boolean(label.trim()) && !error && !customizationResult.error && isDirty
    && (type !== 'RELATION' || !isCreate || (targetObjectId && inverseLabel.trim()));

  const backToObject = () => navigate(`/settings/objects/${objectMetadata.namePlural}`);

  /** @param {Record<string, string | null> | undefined} optionMigrations */
  const persist = async (optionMigrations) => {
    setIsSaving(true);
    try {
      if (isCreate) {
        /** @type {Record<string, unknown>} */
        const input = { objectMetadataId: objectMetadata.id, type, name: apiName, label: label.trim(), icon, description: description.trim() || undefined, ...customizationResult.changes };
        if (SELECT_TYPES.includes(type)) {
          input.options = toApiOptions(options);
          if (defaultValue) input.defaultValue = type === 'SELECT' ? `'${defaultValue}'` : undefined;
        }
        if (type === 'RELATION') {
          const target = relationTargets.find((object) => object.id === targetObjectId);
          input.relation = {
            type: relationType, targetObjectMetadataId: targetObjectId, targetFieldLabel: inverseLabel.trim(),
            targetFieldName: computeMetadataNameFromLabel(inverseLabel) || undefined, targetFieldIcon: objectMetadata.icon ?? undefined,
          };
          if (!target) throw new Error('Missing relation target');
        }
        await createFieldMetadata(input);
        showSuccessSnackbar(t('dataModel.fields.created'));
        backToObject();
        return;
      }
      const currentField = /** @type {FieldMetadata} */ (field);
      /** @type {Record<string, unknown>} */
      const patch = { icon, description: description.trim() || null };
      if (label !== originalLabel) patch.label = label.trim();
      if (canRename && apiName !== currentField.name) patch.name = apiName;
      Object.assign(patch, customizationResult.changes);
      if (SELECT_TYPES.includes(type) && !isFunnelField) {
        patch.options = toApiOptions(options);
        if (optionMigrations && Object.keys(optionMigrations).length > 0) patch.optionMigrations = optionMigrations;
        if (type === 'SELECT' && defaultValue !== unquote(currentField.defaultValue)) patch.defaultValue = defaultValue ? `'${defaultValue}'` : null;
      }
      const updated = await updateFieldMetadata(currentField.id, patch);
      showSuccessSnackbar(t('dataModel.common.saved'));
      setRemoval(null);
      if (updated?.name && updated.name !== currentField.name) navigate(`/settings/objects/${objectMetadata.namePlural}/${updated.name}`, { replace: true });
    } catch (saveError) {
      showDataModelError(saveError);
    } finally {
      setIsSaving(false);
    }
  };

  const save = async () => {
    if (!canSave) return;
    if (field && SELECT_TYPES.includes(type) && !isFunnelField) {
      const removed = findRemovedOptions(field.options ?? [], options);
      if (removed.length > 0) {
        const currentUsage = usage ?? await fetchFieldOptionUsage(field.id).catch(() => ({}));
        const inUse = removed.filter((option) => (currentUsage[option.value] ?? 0) > 0);
        // opções sem registros saem direto; as que têm registros pedem destino
        if (inUse.length > 0) {
          setRemoval({ removed: inUse, usage: currentUsage });
          return;
        }
      }
    }
    persist(undefined);
  };

  const toggleActive = async () => {
    if (!field) return;
    setIsSaving(true);
    try {
      await updateFieldMetadata(field.id, { isActive: field.isActive === false });
      showSuccessSnackbar(field.isActive === false ? t('dataModel.fields.activated') : t('dataModel.fields.deactivated'));
    } catch (toggleError) {
      showDataModelError(toggleError);
    } finally {
      setIsSaving(false);
    }
  };

  const removeField = async () => {
    if (!field) return;
    setIsSaving(true);
    try {
      await deleteFieldMetadata(field.id);
      showSuccessSnackbar(t('dataModel.fields.deleted'));
      backToObject();
    } catch (deleteError) {
      showDataModelError(deleteError);
      setIsSaving(false);
    }
  };

  const breadcrumb = html`<${DataModelBreadcrumb} items=${[rootBreadcrumbItem(), objectBreadcrumbItem(objectMetadata), { label: isCreate ? t('dataModel.fields.newTitle') : originalLabel }]} />`;
  const actions = html`<div class="data-model-header-actions">
    <${Button} title=${isCreate ? t('common.cancel') : t('common.back')} variant="secondary" size="small" onClick=${backToObject} />
    ${isEditable ? html`<${Button} title=${t('common.save')} icon="IconCheck" accent="blue" size="small" disabled=${!canSave} isLoading=${isSaving} onClick=${save} />` : null}
  </div>`;
  const isLabelIdentifier = objectMetadata.labelIdentifierFieldMetadataId === field?.id;

  return html`<${PageCard} header=${html`<${PageHeader} breadcrumb=${breadcrumb} actions=${actions} />`}>
    <form class="settings-page" onSubmit=${(/** @type {Event} */ event) => {
      event.preventDefault();
      save();
    }}>
      <div class="data-model-object-title">
        <${Icon} name=${getFieldIcon(/** @type {any} */ ({ type, icon }))} size=${20} />
        <h1>${t(`fieldTypes.${type}`)}</h1>
        ${isCreate && onChangeType ? html`<button type="button" class="data-model-link-button" onClick=${onChangeType}>${t('dataModel.fields.changeType')}</button>` : null}
      </div>
      <${SettingsSection} title=${t('dataModel.fields.nameTitle')} description=${t('dataModel.fields.nameDescription')}>
        <div class="data-model-name-row" data-columns="2">
          <${IconPicker} value=${icon} onChange=${setIcon} disabled=${!isEditable} />
          <label class="form-field">
            <span class="form-field-label">${t('dataModel.fields.label')}</span>
            <input class="text-input" value=${label} disabled=${!isEditable} placeholder=${t('dataModel.fields.labelPlaceholder')} maxlength="255" autofocus=${isCreate}
              onInput=${(event) => setLabel(/** @type {HTMLInputElement} */ (event.currentTarget).value)} />
          </label>
        </div>
        <div class="data-model-api-name-row">
          <label class="form-field">
            <span class="form-field-label">${t('dataModel.fields.apiName')}</span>
            <input class="text-input settings-table-mono" value=${canRename ? apiName : field?.name ?? ''} disabled=${!isEditable || !canRename || isNameSynced} maxlength="63"
              onInput=${(event) => setName(/** @type {HTMLInputElement} */ (event.currentTarget).value)} />
          </label>
          ${canRename && isEditable
            ? html`<button type="button" class="data-model-link-button" onClick=${() => {
                if (isNameSynced) setName(apiName);
                setIsNameSynced(!isNameSynced);
              }}>${isNameSynced ? t('dataModel.fields.editApiName') : t('dataModel.fields.syncApiName')}</button>`
            : null}
        </div>
        ${!canRename && field ? html`<span class="input-hint">${field.isCustom ? t('dataModel.fields.apiNameLockedType') : t('dataModel.fields.apiNameLockedStandard')}</span>` : null}
        ${error ? html`<div class="form-error" role="alert"><${Icon} name="IconAlertTriangle" size=${14} /><span>${error}</span></div>` : null}
      <//>
      ${SELECT_TYPES.includes(type)
        ? html`<${SettingsSection} title=${t('dataModel.options.title')} description=${isFunnelField ? t('dataModel.funnel.shortDescription') : t('dataModel.options.description')}>
            ${isFunnelField
              ? html`<div class="data-model-notice"><${Icon} name="IconInfoCircle" size=${16} />
                  <span>${t('dataModel.funnel.fieldNotice')} <${Link} href="/settings/funnel">${t('dataModel.funnel.open')}<//></span></div>`
              : html`<${SelectOptionsEditor} options=${options} onChange=${setOptions} usage=${usage} disabled=${!isEditable}
                  defaultValue=${type === 'SELECT' ? defaultValue : null} onDefaultChange=${type === 'SELECT' ? setDefaultValue : null} />`}
          <//>`
        : null}
      ${hasCustomization
        ? html`<${FieldCustomizationSections} field=${field} type=${type} label=${label.trim()} icon=${icon} draft=${customization}
            options=${toApiOptions(options)} disabled=${!isEditable} isFunnelField=${isFunnelField} onChange=${setCustomization}
            defaultError=${customizationResult.error} />`
        : null}
      ${type === 'RELATION' && isCreate
        ? html`<${SettingsSection} title=${t('dataModel.relation.title')} description=${t('dataModel.relation.description')}>
            <div class="data-model-relation-types" role="radiogroup" aria-label=${t('dataModel.relation.type')}>
              ${['MANY_TO_ONE', 'ONE_TO_MANY'].map((candidate) => html`<button type="button" key=${candidate} role="radio" class="data-model-relation-type"
                aria-checked=${relationType === candidate ? 'true' : 'false'} data-selected=${relationType === candidate ? 'true' : undefined}
                onClick=${() => setRelationType(candidate)}>
                <${Icon} name=${candidate === 'MANY_TO_ONE' ? 'IconRelationManyToOne' : 'IconRelationOneToMany'} size=${16} />
                <span>${t(`dataModel.relation.types.${candidate}`)}</span>
              </button>`)}
            </div>
            <div class="form-field-row">
              <label class="form-field">
                <span class="form-field-label">${t('dataModel.relation.target')}</span>
                <select class="text-input data-model-select" value=${targetObjectId} onChange=${(event) => setTargetObjectId(/** @type {HTMLSelectElement} */ (event.currentTarget).value)}>
                  ${relationTargets.map((object) => html`<option key=${object.id} value=${object.id}>${translateMetadataLabel(object.labelSingular)}</option>`)}
                </select>
              </label>
              <label class="form-field">
                <span class="form-field-label">${t('dataModel.relation.inverseLabel')}</span>
                <input class="text-input" value=${inverseLabel} maxlength="255" onInput=${(event) => setInverseLabel(/** @type {HTMLInputElement} */ (event.currentTarget).value)} />
              </label>
            </div>
          <//>`
        : null}
      <${SettingsSection} title=${t('dataModel.common.description')} description=${t('dataModel.fields.descriptionHint')}>
        <textarea class="text-input data-model-textarea" value=${description} disabled=${!isEditable} placeholder=${t('dataModel.common.descriptionPlaceholder')}
          aria-label=${t('dataModel.common.description')} onInput=${(event) => setDescription(/** @type {HTMLTextAreaElement} */ (event.currentTarget).value)}></textarea>
      <//>
      ${field && isEditable && !isSystemField
        ? html`<${DangerZone} title=${t('dataModel.common.dangerZone')} description=${field.isCustom ? t('dataModel.fields.dangerCustom') : t('dataModel.fields.dangerStandard')}>
            ${isFunnelField || isLabelIdentifier
              ? html`<span class="input-hint">${isFunnelField ? t('dataModel.fields.funnelLocked') : t('dataModel.fields.labelIdentifierLocked')}</span>`
              : html`<${Button} title=${field.isActive === false ? t('dataModel.common.activate') : t('dataModel.common.deactivate')}
                  icon=${field.isActive === false ? 'IconEye' : 'IconEyeOff'} variant="secondary" size="small" isLoading=${isSaving} onClick=${toggleActive} />`}
            ${field.isCustom && !isLabelIdentifier
              ? html`<${Button} title=${t('dataModel.common.delete')} icon="IconTrash" variant="secondary" accent="danger" size="small" onClick=${() => setIsDeleteOpen(true)} />`
              : null}
          <//>`
        : null}
    </form>
    ${removal
      ? html`<${OptionRemovalModal} removedOptions=${removal.removed} targetOptions=${toApiOptions(options)} usage=${removal.usage}
          allowEmpty=${field?.isNullable !== false} isSaving=${isSaving} onCancel=${() => setRemoval(null)} onConfirm=${(migrations) => persist(migrations)} />`
      : null}
    ${isDeleteOpen && field
      ? html`<${Modal} title=${t('dataModel.fields.deleteTitle', { field: originalLabel })}
          subtitle=${t('dataModel.fields.deleteSubtitle', { object: translateMetadataLabel(objectMetadata.labelPlural), word: DELETE_CONFIRMATION_WORD })}
          confirmLabel=${t('dataModel.common.delete')} confirmAccent="danger" isLoading=${isSaving}
          isConfirmDisabled=${confirmation.trim().toLowerCase() !== DELETE_CONFIRMATION_WORD}
          onClose=${() => setIsDeleteOpen(false)} onConfirm=${removeField}>
          <input class="text-input" value=${confirmation} placeholder=${DELETE_CONFIRMATION_WORD} autofocus aria-label=${t('dataModel.common.typeToConfirm')}
            onInput=${(event) => setConfirmation(/** @type {HTMLInputElement} */ (event.currentTarget).value)} />
        <//>`
      : null}
  <//>`;
}

/**
 * @param {{ objectNamePlural: string }} props
 */
export function SettingsFieldNewPage({ objectNamePlural }) {
  const objectMetadata = findObjectByNamePlural(objectNamePlural);
  const [type, setType] = useState(/** @type {string | null} */ (null));
  useEffect(() => {
    document.title = `${t('dataModel.fields.newTitle')} - ${t('navigation.settings')}`;
  }, []);
  if (!objectMetadata || isHiddenModuleMetadata(objectMetadata, isTenantModuleEnabled)) return html`<${NotFoundPage} />`;
  if (type) return html`<${FieldForm} key=${type} objectMetadata=${objectMetadata} field=${null} type=${type} onChangeType=${() => setType(null)} />`;
  const breadcrumb = html`<${DataModelBreadcrumb} items=${[rootBreadcrumbItem(), objectBreadcrumbItem(objectMetadata), { label: t('dataModel.fields.newTitle') }]} />`;
  return html`<${PageCard} header=${html`<${PageHeader} breadcrumb=${breadcrumb}
    actions=${html`<${Button} title=${t('common.cancel')} variant="secondary" size="small" onClick=${() => navigate(`/settings/objects/${objectMetadata.namePlural}`)} />`} />`}>
    <${FieldTypeStep} objectMetadata=${objectMetadata} onSelect=${setType} />
  <//>`;
}

/**
 * @param {{ objectNamePlural: string, fieldName: string }} props
 */
export function SettingsFieldDetailPage({ objectNamePlural, fieldName }) {
  const objectMetadata = findObjectByNamePlural(objectNamePlural);
  const field = objectMetadata?.fields.find((candidate) => candidate.name === fieldName);
  useEffect(() => {
    if (field) document.title = `${translateMetadataLabel(field.label)} - ${t('navigation.settings')}`;
  }, [field?.id]);
  // objeto ou campo de módulo desligado: para a escola sem o módulo, não existe
  if (!objectMetadata || !field || isHiddenModuleMetadata(objectMetadata, isTenantModuleEnabled) || isHiddenModuleMetadata(field, isTenantModuleEnabled)) {
    return html`<${NotFoundPage} />`;
  }
  // a chave inclui a versão dos metadados do campo, para o formulário recomeçar depois de salvar
  return html`<${FieldForm} key=${`${field.id}-${JSON.stringify([field.label, field.options, field.isActive, field.defaultValue, field.icon, field.description, field.settings, field.isUnique])}`}
    objectMetadata=${objectMetadata} field=${field} type=${field.type} />`;
}
